import { useResourceQueryState as useQueryState } from '@/hooks/useResourceQueryState';
import { reportQuery } from '@/queries';
import { PERMISSIONS, PermissionTuple } from '@/shared/permissions';
import { useListViewStore, useReportStore } from '@/stores';
import type { ReportComponent } from '@/types';

import { getAccessState, useCASL } from './useCASL';
import { useWorkspaceResourceAccess, WorkspaceResourceAction } from './useWorkspaceResourceAccess';

export type NestedResource = 'reports' | 'listViews';
export type NestedResourceAction = 'read' | 'update' | 'comment' | 'manage' | 'invite';

type NestedResourcePermissions = Record<NestedResourceAction, PermissionTuple>;

export const nestedResourcePermissions: Record<NestedResource, NestedResourcePermissions> = {
    reports: {
        read: PERMISSIONS.REPORT.READ,
        update: PERMISSIONS.REPORT.UPDATE,
        comment: PERMISSIONS.REPORT.COMMENT,
        manage: PERMISSIONS.REPORT.MANAGE,
        invite: PERMISSIONS.REPORT.INVITE,
    },
    listViews: {
        read: PERMISSIONS.LISTVIEW.READ,
        update: PERMISSIONS.LISTVIEW.UPDATE,
        comment: PERMISSIONS.LISTVIEW.COMMENT,
        manage: PERMISSIONS.LISTVIEW.MANAGE,
        invite: PERMISSIONS.LISTVIEW.INVITE,
    },
};

const deniedAccessState = {
    allowed: false,
    hidden: true,
    disabled: true,
    readOnly: true,
    canRead: false,
    canInteract: false,
    canMutate: false,
    reason: 'NESTED_RESOURCE_NOT_LOADED',
} as const;

const lockedAccessState = {
    allowed: false,
    hidden: false,
    disabled: true,
    readOnly: true,
    canRead: true,
    canInteract: false,
    canMutate: false,
    reason: 'RESOURCE_LOCKED',
} as const;

const parentResourceUnavailableAccessState = {
    ...deniedAccessState,
    reason: 'PARENT_RESOURCE_NOT_LOADED',
} as const;

export const getNestedResourcePermission = (resource: NestedResource, action: NestedResourceAction) =>
    nestedResourcePermissions[resource][action];

export const useNestedResourceAccess = (resource: NestedResource, resourceId?: string | null) => {
    const { ability } = useCASL();
    const { listViews, selectedListView } = useListViewStore();
    const { reports, selectedReport } = useReportStore();
    const loadedListView =
        listViews.find((listView) => listView.id === resourceId) ??
        (selectedListView?.id === resourceId ? selectedListView : undefined);
    const loadedReport =
        reports.find((report) => report.id === resourceId) ??
        (selectedReport?.id === resourceId ? selectedReport : undefined);
    const parentResource = resource === 'listViews' ? 'lists' : 'dashboards';
    const parentResourceId = resource === 'listViews' ? loadedListView?.list?.id : loadedReport?.dashboard?.id;
    const parentAccess = useWorkspaceResourceAccess(parentResource, parentResourceId);
    const locked = (resource === 'listViews' ? loadedListView?.locked : loadedReport?.locked) === true;

    const accessState = (action: NestedResourceAction) => {
        if (!resourceId) {
            return deniedAccessState;
        }

        if (!parentResourceId) {
            return parentResourceUnavailableAccessState;
        }

        const nestedAccess = getAccessState(ability, getNestedResourcePermission(resource, action), {
            instance: { id: resourceId },
        });

        if (!nestedAccess.allowed) {
            return nestedAccess;
        }

        const parentActionAccess = parentAccess[action as WorkspaceResourceAction];

        if (!parentActionAccess.allowed) {
            return parentActionAccess;
        }

        if (locked && action === 'update') {
            return lockedAccessState;
        }

        return nestedAccess;
    };

    const read = accessState('read');
    const update = accessState('update');
    const comment = accessState('comment');
    const manage = accessState('manage');
    const invite = accessState('invite');

    return {
        read,
        update,
        comment,
        manage,
        invite,
        canRead: read.allowed,
        canUpdate: update.allowed,
        canComment: comment.allowed,
        canManage: manage.allowed,
        canInvite: invite.allowed,
        canCreateSibling: parentAccess.update.allowed,
        createSibling: parentAccess.update,
        locked,
    };
};

export const useReportComponentAccess = (component?: Pick<ReportComponent, 'report'> | null) => {
    const [reportId] = useQueryState('report', reportQuery);
    const resolvedReportId = component?.report?.id ?? reportId ?? undefined;

    return {
        ...useNestedResourceAccess('reports', resolvedReportId),
        reportId: resolvedReportId,
    };
};
